// src/pages/EvasionDashboard.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaPlane,
  FaTrash,
  FaPause,
  FaPlay,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaExclamationCircle,
  FaSearch,
  FaArrowRight,
} from 'react-icons/fa';
import { supabase } from '../supabaseClient';
import { deleteSmartAlert, listSmartAlerts, toggleSmartAlert } from '../lib/smartAlerts';
import Header from '../components/Header';
import Footer from '../components/Footer';

const copy = {
  fr: {
    badge: 'Espace Voyageur',
    titleA: 'Mes Alertes',
    titleB: 'Évasion',
    subtitle: 'Gère les destinations que Forfeo surveille pour toi.',
    newAlert: 'Nouvelle alerte',
    loading: 'Chargement de tes radars...',
    loadError: 'Impossible de charger tes alertes pour le moment.',
    updateError: 'Impossible de mettre à jour cette alerte pour le moment.',
    deleteError: 'Impossible de supprimer cette alerte pour le moment.',
    confirmDelete: 'Es-tu sûr de vouloir supprimer cette alerte définitivement ?',
    emptyTitle: 'Ton radar est vide',
    emptyText: "Tu n'as aucune alerte active. Laisse Forfeo surveiller les prix pour ton prochain voyage !",
    firstAlert: 'Créer ma première alerte',
    active: 'En surveillance',
    paused: 'En pause',
    createdOn: 'Créée le',
    pause: 'Mettre en pause',
    reactivate: 'Réactiver',
    delete: "Supprimer l'alerte",
    budget: 'Budget Max',
    period: 'Période',
    any: 'Peu importe',
    lastScanToday: "Dernière analyse : Aujourd'hui",
    pendingScan: 'Analyse Forfeo en attente...',
    directOnly: 'Vols directs',
  },
  en: {
    badge: 'Traveler Space',
    titleA: 'My Escape',
    titleB: 'Alerts',
    subtitle: 'Manage the destinations Forfeo watches for you.',
    newAlert: 'New alert',
    loading: 'Loading your radars...',
    loadError: 'Unable to load your alerts right now.',
    updateError: 'Unable to update this alert right now.',
    deleteError: 'Unable to delete this alert right now.',
    confirmDelete: 'Are you sure you want to permanently delete this alert?',
    emptyTitle: 'Your radar is empty',
    emptyText: 'You do not have any active alerts. Let Forfeo watch prices for your next trip!',
    firstAlert: 'Create my first alert',
    active: 'Watching',
    paused: 'Paused',
    createdOn: 'Created on',
    pause: 'Pause',
    reactivate: 'Reactivate',
    delete: 'Delete alert',
    budget: 'Max Budget',
    period: 'Period',
    any: 'Anytime',
    lastScanToday: 'Last scan: Today',
    pendingScan: 'Forfeo analysis pending...',
    directOnly: 'Direct flights',
  },
};

const EvasionDashboard = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'fr';
  const text = copy[lang];

  const [alerts, setAlerts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAlerts = async () => {
    try {
      setError(null);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/login?redirect=/mes-alertes');
        return;
      }

      const response = await listSmartAlerts();
      if (!response.ok) throw new Error(response.details || response.error || text.loadError);
      const flightAlerts = (response.alerts || []).filter((alert: any) => alert.alert_type === 'flight');
      setAlerts(flightAlerts);
    } catch (err: any) {
      console.error('Erreur lors de la récupération des alertes:', err);
      setError(text.loadError);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (id: string, currentStatus: string) => {
    const newStatus = currentStatus === 'active' ? 'paused' : 'active';
    setAlerts((prev) => prev.map((a) => a.id === id ? { ...a, status: newStatus } : a));

    try {
      const response = await toggleSmartAlert(id, newStatus as any);
      if (!response.ok) throw new Error(response.details || response.error || text.updateError);
    } catch (err) {
      console.error('Erreur lors de la mise à jour:', err);
      setError(text.updateError);
      fetchAlerts();
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(text.confirmDelete)) return;

    setAlerts((prev) => prev.filter((a) => a.id !== id));

    try {
      const response = await deleteSmartAlert(id);
      if (!response.ok) throw new Error(response.details || response.error || text.deleteError);
    } catch (err) {
      console.error('Erreur lors de la suppression:', err);
      setError(text.deleteError);
      fetchAlerts();
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col font-sans">
      <Header />

      <main className="flex-grow pt-24 pb-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sky-100 text-sky-700 text-xs font-bold uppercase tracking-wider mb-4">
                <FaPlane /> {text.badge}
              </div>
              <h1 className="text-3xl md:text-5xl font-extrabold text-slate-900 tracking-tight">
                {text.titleA} <span className="text-sky-600">{text.titleB}</span>
              </h1>
              <p className="text-slate-500 mt-2 text-lg">{text.subtitle}</p>
            </div>

            <Link to="/evasion" className="px-6 py-3 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 transition-all flex items-center justify-center gap-2 shadow-lg hover:shadow-xl hover:-translate-y-0.5">
              <FaSearch /> {text.newAlert}
            </Link>
          </div>

          {error && (
            <div className="mb-8 p-4 bg-red-50 text-red-600 rounded-2xl flex items-center gap-3 font-medium border border-red-100">
              <FaExclamationCircle /> {error}
            </div>
          )}

          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-12 h-12 border-4 border-sky-200 border-t-sky-600 rounded-full animate-spin mb-4" />
              <p className="text-slate-400 font-medium">{text.loading}</p>
            </div>
          ) : alerts.length === 0 ? (
            <div className="bg-white rounded-[32px] border border-slate-100 p-12 text-center shadow-xl shadow-slate-200/20">
              <div className="w-24 h-24 bg-sky-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <FaPlane className="text-sky-400 text-4xl" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">{text.emptyTitle}</h3>
              <p className="text-slate-500 mb-8 max-w-md mx-auto">{text.emptyText}</p>
              <Link to="/evasion" className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-sky-600 to-indigo-600 text-white rounded-2xl font-bold text-lg hover:shadow-lg transition-all">
                {text.firstAlert}
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {alerts.map((alert) => (
                <div key={alert.id} className={`bg-white rounded-3xl p-6 border transition-all duration-300 ${alert.status === 'active' ? 'border-sky-100 shadow-xl shadow-sky-900/5 hover:-translate-y-1' : 'border-slate-200 shadow-sm opacity-75 grayscale-[0.5]'}`}>
                  <div className="flex justify-between items-start mb-6">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-xl ${alert.status === 'active' ? 'bg-sky-50 text-sky-600' : 'bg-slate-100 text-slate-400'}`}>
                        <FaPlane className="-rotate-45" />
                      </div>
                      <div>
                        <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${alert.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                          {alert.status === 'active' ? text.active : text.paused}
                        </span>
                        <p className="text-xs text-slate-400 mt-1">{text.createdOn} {new Date(alert.created_at).toLocaleDateString(lang === 'en' ? 'en-CA' : 'fr-CA')}</p>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <button onClick={() => handleToggleStatus(alert.id, alert.status)} className={`p-2 rounded-xl transition-colors ${alert.status === 'active' ? 'text-amber-500 hover:bg-amber-50' : 'text-green-500 hover:bg-green-50'}`} title={alert.status === 'active' ? text.pause : text.reactivate}>
                        {alert.status === 'active' ? <FaPause /> : <FaPlay />}
                      </button>
                      <button onClick={() => handleDelete(alert.id)} className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-colors" title={text.delete}>
                        <FaTrash />
                      </button>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h3 className="text-2xl font-black text-slate-900 mb-1 flex items-center gap-2">
                      {alert.metadata?.flight?.origin || alert.origin} <FaArrowRight className="text-slate-300 text-sm" /> {alert.metadata?.flight?.destination || alert.destination}
                    </h3>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-50 p-3 rounded-2xl">
                      <div className="flex items-center gap-2 text-xs font-bold text-slate-400 uppercase mb-1"><FaMoneyBillWave /> {text.budget}</div>
                      <div className="text-lg font-bold text-slate-900">{alert.max_budget ?? alert.metadata?.flight?.max_budget ?? alert.metadata?.flight?.budget ?? text.any} $</div>
                    </div>
                    <div className="bg-slate-50 p-3 rounded-2xl">
                      <div className="flex items-center gap-2 text-xs font-bold text-slate-400 uppercase mb-1"><FaCalendarAlt /> {text.period}</div>
                      <div className="text-sm font-bold text-slate-900 truncate">{alert.metadata?.flight?.travel_period || alert.travel_period || text.any}</div>
                    </div>
                  </div>

                  <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                    <span>{alert.last_sent_at ? text.lastScanToday : text.pendingScan}</span>
                    {(alert.metadata?.flight?.direct_only || alert.direct_only) && <span className="font-medium text-slate-700 bg-slate-100 px-2 py-1 rounded-md">{text.directOnly}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default EvasionDashboard;
